// The client's connection to a room: the WebSocket, the room and
// snapshot state it carries, and the input it sends back. Everything
// here is networking and data — presentation (particles, the HUD, the
// console) lives in main.js and ui.js and reacts to this through the
// hooks passed to connect(). Keeping the two apart is what lets
// main.js read `client.cur` instead of juggling a dozen module
// globals.

import { WORLD_SEED } from "../../../engine/chunks.js";
import { createTerrain } from "../../../engine/terrain.js";
import { mergeServerWeapons } from "../../../content/index.js";

export class Client {
    constructor() {
        this.ws = null;
        this.selfCid = null;
        this.prev = null; // previous snapshot, for interpolation
        this.cur = null; // latest snapshot
        this.curTime = 0; // performance.now() when cur arrived
        this.seed = WORLD_SEED;
        this.gen = "original"; // which world generator the room uses (from `welcome`)
        this.floor = "lava";   // the room's floor mode (from `welcome`) — decides the void line
        // Our mirror of the room's live terrain: base generation +
        // the server's edits, rebuilt on join with the full edit set
        // and kept in step by per-tick deltas on the fx channel. The
        // renderer draws from it, so a dug tile disappears.
        this.terrain = createTerrain(this.seed, this.gen, this.floor);
        this.room = null; // the room we're in, from `welcome`
        this.roomList = []; // what this server offers, for the join panel
        this.hudWidgets = []; // reactive-HUD readouts this room's plugins declared (from `welcome`)
        this.controls = [];   // controls this room's plugins declared (from `welcome`)
        this.menus = [];      // template menus this room's plugins declared (from `welcome`)
        this.clientScripts = []; // plugin client.lua scripts to draw custom sprites (from `welcome`)
        this.clientImages = []; // plugin image assets (data URIs) for the VM's image() primitive (from `welcome`)
        this.itemManifest = {}; // item name -> { icon, tint, label, category } for the inventory faces (from `welcome`)
        this.hotbar = true;     // whether the default top-left hotbar is shown (a plugin can suppress it)
        this.heldItem = null;   // the item you're holding (Set 12.11) — any item name; a hotbar plugin sets it
                                // via hold(). Selection state, client-only; input.weapon is DERIVED from it.
        this.pickedRoom = null; // the id the lobby has selected to join
        this.input = { up: 0, down: 0, left: 0, right: 0, ax: 0, ay: 0, fire: 0, weapon: 0, held: "", use: 0, jet: 0, shift: 0, ctl: [], menu: [] };
        // Clock sync for the shared music timeline: serverOffset ≈ serverClock − ourClock,
        // estimated from the lowest-latency ping/pong we've seen (the least jittered sample).
        this.serverOffset = 0;
        this._bestRtt = Infinity;
        this.music = null;
    }

    get connected() { return !!this.ws && this.ws.readyState === 1; }
    send(msg) { if (this.connected) this.ws.send(JSON.stringify(msg)); }

    // Ping the server periodically to keep the clock estimate fresh (latency drifts). Each
    // pong refines serverOffset; the first goes out the moment we connect so music syncs fast.
    _startPings() {
        this._bestRtt = Infinity;
        const ping = () => this.send({ t: "ping", c: Date.now() });
        ping();
        clearInterval(this._pingTimer);
        this._pingTimer = setInterval(ping, 4000);
    }

    // Open a connection and route its messages. `hooks` are the
    // presentation's ears:
    // onConnecting/onWelcome/onState/onChat/onNotice/onJoinFail/onClose/onError.
    // The Client updates its OWN state first, so a hook always sees
    // consistent data.
    connect(name, color, server, hooks = {}) {
        const host = (server || "").trim();
        let url;
        if (host.startsWith("ws://") || host.startsWith("wss://")) url = host;
        else {
            const proto = location.protocol === "https:" ? "wss" : "ws";
            url = `${proto}://${host || location.host}`;
        }
        if (hooks.onConnecting) hooks.onConnecting();
        try {
            this.ws = new WebSocket(url);
        } catch (e) {
            if (hooks.onError) hooks.onError("bad server URL");
            return;
        }
        this.ws.onopen = () => { this.send({ t: "join", name, color, room: this.pickedRoom }); this._startPings(); };
        this.ws.onmessage = (ev) => this._dispatch(JSON.parse(ev.data), hooks);
        this.ws.onclose = () => { this.selfCid = null; clearInterval(this._pingTimer); if (hooks.onClose) hooks.onClose(); };
        this.ws.onerror = () => { if (hooks.onError) hooks.onError("connection failed — check the server URL"); };
    }

    _dispatch(m, hooks) {
        if (m.t === "welcome") {
            this.selfCid = m.cid;
            if (m.seed != null) this.seed = m.seed;
            if (m.gen != null) this.gen = m.gen;
            if (m.floor != null) this.floor = m.floor;
            this.room = m.room;
            if (m.rooms) this.roomList = m.rooms;
            this.hudWidgets = m.hudWidgets || []; // reactive-HUD readouts declared by this room's plugins
            this.controls = m.controls || [];     // controls declared by this room's plugins
            this.menus = m.menus || [];           // template menus declared by this room's plugins
            this.clientScripts = m.clientScripts || []; // plugin client.lua scripts for custom sprites
            this.clientImages = m.clientImages || [];   // plugin image assets (data URIs) for image()
            this.itemManifest = m.items || {};          // item icon/label per name, for the inventory faces
            this.hotbar = m.hotbar !== false;           // default hotbar on unless a plugin suppressed it
            mergeServerWeapons(m.weapons);              // fold plugin guns (grenade) into the client's weapon tables
            this.heldItem = null;                       // new room / loadout → held resets (falls back to your gun)

            // Rebuild the terrain mirror for this room (its generator + seed + floor) and
            // lay the server's current edits over it, so the very first frame
            // draws the mutated world, not the pristine one.
            // Switching rooms mid-session: drop the old room's
            // snapshots rather than interpolate between two different
            // worlds for a frame.
            this.terrain = createTerrain(this.seed, this.gen, this.floor);
            this.terrain.applyEdits(m.edits || []);
            this.terrain.applyDamage(m.damage || []);
            this.terrain.applyPaint(m.paints || []);   // Set 19: the room's paint splatter
            this.terrain.applyBlockDefs(m.blockdefs || []); // Set 19: custom-block designs (before the placements that reference them)
            this.terrain.applyCustom(m.customs || []); // Set 19: placed custom blocks
            this.prev = this.cur = null;
            if (hooks.onWelcome) hooks.onWelcome(m);
        } else if (m.t === "state") {
            this.prev = this.cur;
            this.cur = m;
            this.curTime = performance.now();
            if (hooks.onState) hooks.onState(m);
        } else if (m.t === "chat") {
            if (hooks.onChat) hooks.onChat(m);
        } else if (m.t === "music") {
            this.music = { i: m.i, startedAt: m.startedAt };
            if (hooks.onMusic) hooks.onMusic(this.music);
        } else if (m.t === "pong") {
            const rtt = Date.now() - m.c;
            // Keep the offset from the LEAST-jittered round-trip; assume symmetric latency.
            if (rtt < this._bestRtt) { this._bestRtt = rtt; this.serverOffset = m.serverNow + rtt / 2 - Date.now(); }
            if (this.music && hooks.onMusic) hooks.onMusic(this.music); // re-seek with the sharper clock
        } else if (m.t === "notice") {
            if (hooks.onNotice) hooks.onNotice(m);
        } else if (m.t === "joinfail") {
            if (hooks.onJoinFail) hooks.onJoinFail(m);
        }
    }

    // Push the current input, stamping the world-space aim point.
    // Called on the 33ms tick and again on a fresh click/tap, so a
    // quick tap isn't dropped.
    sendInput(aim) {
        if (!this.connected || this.selfCid == null) return;
        if (aim) { this.input.ax = aim.x; this.input.ay = aim.y; } // null under an overlay — keep the last aim
        this.input.held = this.heldItem || "";   // what the mouse acts ON (Set 14's use seam) — any item
        this.send({ t: "input", ...this.input });
        if (this.input.ctl.length) this.input.ctl = [];   // edge control presses are one-shot — clear after sending
        if (this.input.menu.length) this.input.menu = []; // menu selections likewise
    }

    // Queue a plugin control press (an edge action) to ride the next input send, and flush it now so a
    // tap isn't dropped. `aim` lets the immediate send stamp the current aim like a click does.
    pressControl(id, aim) { this.input.ctl.push(id); this.sendInput(aim); }

    // Send a template-menu selection (UI framework D): the menu id + the chosen option, flushed now.
    selectMenu(id, choice, aim) { this.input.menu.push({ id, choice }); this.sendInput(aim); }

    // Tell the server which block the trowel should place (chosen from the wheel). The
    // server validates the material; the choice comes back in our next snapshot's `blocks`.
    selectBlock(sel) { this.send({ t: "block", sel }); }
    // Client-plugin runtime (Set 12.5): a plugin's client.lua send(event, data) -> its own server half.
    sendPluginMessage(plugin, event, data) { this.send({ t: "pmsg", plugin, event, data }); }

    // Our own row in the latest snapshot's scoreboard, or null before
    // we're in.
    selfScore() {
        if (!this.cur) return null;
        for (const s of this.cur.scores) if (s.cid === this.selfCid) return s;
        return null;
    }
}
